import { FilterList, Search } from '@mui/icons-material';
import { Autocomplete, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useGetOrganizationCardOwnersQuery, useGetOrganizationQuery, useSearchCardOwnersQuery } from '../../api/organizationSlice';
import LoadingIndicator from '../LoadingIndicator';
import GenericTable from '../Tables/GenericTable';
import useTableSearch from '../Hooks/useTableSearch';
import { theme } from '../../theme';

function Physicians() {
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const {
        searchTerm,
        filter: selectedSpecialty,
        handleSearchTermChange,
        handleFilterChange,
        page,
        setPage,
        resetSearch
    } = useTableSearch('physicians');
    const { data: organization } = useGetOrganizationQuery(organizationId);
    const { data: allPhysiciansResponse = [] } = useGetOrganizationCardOwnersQuery(organizationId);
    const {
        data: searchedPhysicians = {},
        isLoading,
        isSuccess
    } = useSearchCardOwnersQuery({
        organizationId,
        page,
        searchTerm: encodeURIComponent(searchTerm ?? ''),
        filter: encodeURIComponent(selectedSpecialty ?? '')
    }, {
        skip: !organizationId
    });

    const allPhysicians = useMemo(() => {
        if (Array.isArray(allPhysiciansResponse)) {
            return allPhysiciansResponse;
        }
        return allPhysiciansResponse?.data ?? [];
    }, [allPhysiciansResponse]);

    const physicians = useMemo(() => searchedPhysicians?.data ?? [], [searchedPhysicians]);
    const totalPhysicians = searchedPhysicians?.meta?.total ?? physicians.length;

    const sortedSpecialties = useMemo(() => {
        return [...new Set(allPhysicians
            .map((physician) => physician.specialty)
            .filter(Boolean))].sort((a, b) => a.localeCompare(b));
    }, [allPhysicians]);

    useEffect(() => {
        if (isSuccess) {
            console.log('Organization physicians:', physicians);
        }
    }, [isSuccess, physicians]);

    useEffect(() => {
        resetSearch();
    }, [organizationId]);

    const tableHeaders = [
        'Name',
        'Specialty',
        '# of Cards',
        'Status',
        'Included in Campaigns?',
        'Mobile Status',
        '',
    ];
    
    return (
        isLoading && <LoadingIndicator /> ||
        <Stack spacing={2} pb={2} sx={{ height: '100%' }}>
            <Stack px={{ xs: 2, md: 6 }} py={2} direction='row' justifyContent='space-between' alignItems='center' sx={{ backgroundColor: theme.palette.background.white, borderBottom: 1, borderColor: 'divider' }}>
                <Stack spacing={0.5}>
                    <Typography variant='heavy' sx={{ fontSize: '24px' }}>
                        <strong>Physicians</strong>
                    </Typography>
                    <Typography>
                        Organization: <strong>{organization?.name}</strong>
                    </Typography>
                </Stack>
                <Stack direction='row' justifyContent={{ xs: 'space-between', md: 'start' }} spacing={2} width={{ xs: '100%', md: '50%', lg: '45%' }}>
                    <Paper elevation={2} sx={{ width: '50%' }}>
                        <TextField
                            placeholder='Search Physicians'
                            value={searchTerm}
                            onChangeCapture={handleSearchTermChange}
                            size='small'
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Paper>
                    <Paper elevation={2} sx={{ width: '50%' }}>
                        <Autocomplete
                            options={sortedSpecialties}
                            getOptionLabel={(option) => option}
                            value={selectedSpecialty}
                            onChange={handleFilterChange}
                            size='small'
                            blurOnSelect
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder='Filter by specialty'
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <FilterList />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Paper>
                </Stack>
            </Stack>
            <Stack px={{ xs: 2, md: 6 }} sx={{ minHeight: 0, flex: 1 }}>
                <GenericTable
                    data={physicians}
                    dataType='physician'
                    headers={tableHeaders}
                    isDataLoaded={isSuccess}
                    page={Number(page) - 1}
                    setPage={setPage}
                    total={totalPhysicians}
                />
            </Stack>
        </Stack>
    );
}

export default Physicians;
